class VideoSliderAutoplay {
  constructor() {
    this.currentActiveVideo = null;
    this.isInitialized = false;
    this.mutedVideos = new Set(); // Track which videos are muted
    this.hoverPlayingVideos = new Set(); // Track which videos are playing due to hover
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupVideoSliders());
    } else {
      this.setupVideoSliders();
    }
  }

  setupVideoSliders() {
    // Find all video sliders
    const videoSliders = document.querySelectorAll('.swiper-slider');
    
    if (videoSliders.length === 0) {
      // If no sliders found, try again after a delay
      setTimeout(() => this.setupVideoSliders(), 1000);
      return;
    }
    
    videoSliders.forEach(slider => {
      this.setupSlider(slider);
    });
  }

  setupSlider(slider) {
    // Wait for Swiper to be initialized
    const checkSwiper = setInterval(() => {
      if (slider.swiper) {
        clearInterval(checkSwiper);
        this.initializeSliderEvents(slider.swiper);
        
        // Try to play center video multiple times to ensure it works
        setTimeout(() => {
          this.playCenterSlideVideo(slider.swiper);
        }, 200);
        
        setTimeout(() => {
          this.playCenterSlideVideo(slider.swiper);
        }, 500);
        
        setTimeout(() => {
          this.playCenterSlideVideo(slider.swiper);
        }, 1000);
        
        setTimeout(() => {
          this.playCenterSlideVideo(slider.swiper);
        }, 2000);
        
        // Also try when window loads
        window.addEventListener('load', () => {
          this.playCenterSlideVideo(slider.swiper);
        });
      }
    }, 100);

    // Fallback if Swiper doesn't initialize within 5 seconds
    setTimeout(() => {
      clearInterval(checkSwiper);
    }, 5000);
  }

  initializeSliderEvents(swiper) {
    if (this.isInitialized) return;
    
    // Pause all videos initially
    this.pauseAllVideos(swiper);

    // Setup video loading events
    this.setupVideoLoadingEvents(swiper);

    // Setup mute controls
    this.setupMuteControls(swiper);

    // Play the center slide video immediately on initialization
    this.playCenterSlideVideo(swiper);

    // Also try again after a short delay to ensure everything is ready
    setTimeout(() => {
      this.playCenterSlideVideo(swiper);
    }, 100);

    // Listen for slide changes
    swiper.on('slideChange', () => {
      this.handleSlideChange(swiper);
    });

    // Listen for slide change transitions
    swiper.on('slideChangeTransitionEnd', () => {
      this.handleSlideChangeEnd(swiper);
    });

    // Listen for loop events (when slider loops back to beginning)
    swiper.on('loop', () => {
      this.handleLoopEvent(swiper);
    });

    // Listen for visibility change to pause videos when tab is not active
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllVideos(swiper);
      } else {
        setTimeout(() => {
          this.playCenterSlideVideo(swiper);
        }, 100);
      }
    });

    this.isInitialized = true;
  }

  setupMuteControls(swiper) {
    const muteButtons = swiper.el.querySelectorAll('.mute-btn');
    
    muteButtons.forEach(button => {
      const video = button.closest('.video-container').querySelector('video');
      const videoId = this.getVideoId(video);
      
      // Set initial mute state - all videos start muted
      video.muted = true;
      button.classList.remove('unmuted');
      
      // Add click event listener
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMute(video, button);
      });
    });
  }

  toggleMute(video, button) {
    const videoId = this.getVideoId(video);
    
    if (video.muted) {
      // Unmute the video
      video.muted = false;
      button.classList.add('unmuted');
      this.mutedVideos.delete(videoId);
    } else {
      // Mute the video
      video.muted = true;
      button.classList.remove('unmuted');
      this.mutedVideos.add(videoId);
    }
  }

  getVideoId(video) {
    // Create a unique ID for the video based on its source
    return video.src || video.currentSrc || 'video-' + Math.random();
  }

  handleLoopEvent(swiper) {
    // Handle loop events - when slider loops back to beginning
    setTimeout(() => {
      this.playCenterSlideVideo(swiper);
    }, 100);
  }

  setupVideoLoadingEvents(swiper) {
    const videos = swiper.el.querySelectorAll('video');
    
    videos.forEach(video => {
      // Add loading class
      video.classList.add('loading');
      
      // Set video attributes for autoplay
      video.muted = true; // Start muted by default
      video.playsInline = true;
      video.autoplay = false; // We'll control autoplay manually
      
      // Handle video loading
      video.addEventListener('loadedmetadata', () => {
        video.classList.remove('loading');
        video.classList.add('loaded');
        
        // If this is the center video, try to play it
        const activeSlide = swiper.slides[swiper.activeIndex];
        if (activeSlide && activeSlide.contains(video)) {
          setTimeout(() => {
            this.playCenterSlideVideo(swiper);
          }, 50);
        }
      });

      // Handle video errors
      video.addEventListener('error', (e) => {
        video.classList.remove('loading');
      });

      // Handle video ended (for loop videos)
      video.addEventListener('ended', () => {
        if (video.loop) {
          video.currentTime = 0;
          // Only play if this is still the active video
          if (video === this.currentActiveVideo) {
            video.play().catch(() => {});
          }
        }
      });

      // Handle video play event to track which video is playing
      video.addEventListener('play', () => {
        this.currentActiveVideo = video;
      });

      // Handle video pause event
      video.addEventListener('pause', () => {
        if (video === this.currentActiveVideo) {
          this.currentActiveVideo = null;
        }
      });
      
      // Add hover functionality
      const videoContainer = video.closest('.video-container');
      if (videoContainer) {
        // Play video on hover
        videoContainer.addEventListener('mouseenter', () => {
          if (video.paused) {
            video.muted = true; // Ensure muted for autoplay
            video.play().then(() => {
              this.hoverPlayingVideos.add(video);
            }).catch(error => {
              // Silent fail for hover play
            });
          }
        });
        
        // Pause video when hover ends
        videoContainer.addEventListener('mouseleave', () => {
          // Check if this is not the center active video
          const activeSlide = swiper.slides[swiper.activeIndex];
          const isCenterVideo = activeSlide && activeSlide.contains(video);
          
          if (!isCenterVideo && this.hoverPlayingVideos.has(video)) {
            video.pause();
            this.hoverPlayingVideos.delete(video);
          }
        });
      }
    });
    
    // Add a global click listener to start videos if they haven't started
    const startVideosOnInteraction = () => {
      if (!this.currentActiveVideo) {
        this.playCenterSlideVideo(swiper);
      }
      document.removeEventListener('click', startVideosOnInteraction);
      document.removeEventListener('touchstart', startVideosOnInteraction);
    };
    
    document.addEventListener('click', startVideosOnInteraction, { once: true });
    document.addEventListener('touchstart', startVideosOnInteraction, { once: true });
  }

  handleSlideChange(swiper) {
    // Immediately pause the currently playing video
    if (this.currentActiveVideo) {
      this.currentActiveVideo.pause();
      this.currentActiveVideo = null;
    }
    
    // Pause all hover playing videos
    this.hoverPlayingVideos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
    this.hoverPlayingVideos.clear();
    
    // Also pause all videos to be extra safe
    this.pauseAllVideos(swiper);
  }

  handleSlideChangeEnd(swiper) {
    // Play the center slide video after transition ends
    setTimeout(() => {
      this.playCenterSlideVideo(swiper);
    }, 150); // Slightly longer delay to ensure transition is complete
  }

  pauseAllVideos(swiper) {
    const videos = swiper.el.querySelectorAll('video');
    videos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });
    // Clear the current active video reference
    this.currentActiveVideo = null;
  }

  playCenterSlideVideo(swiper) {
    
    // First, ensure all other videos are paused
    this.pauseAllVideos(swiper);
    
    // Get the currently active slide
    const activeSlide = swiper.slides[swiper.activeIndex];
    
    if (activeSlide) {
      const video = activeSlide.querySelector('video');
      
      if (video) {
        // Check if this video should be muted based on user preference
        const videoId = this.getVideoId(video);
        const shouldBeMuted = this.mutedVideos.has(videoId);
        
        // Ensure video has proper attributes - start muted
        video.muted = true; // Always start muted
        video.playsInline = true;
        video.loop = true;
        
        // Update mute button state
        const muteButton = activeSlide.querySelector('.mute-btn');
        if (muteButton) {
          muteButton.classList.remove('unmuted');
        }
        
        // Try to play the video regardless of readyState
        const playVideo = () => {
          
          // Try different strategies
          const strategies = [
            () => video.play(),
            () => {
              video.currentTime = 0;
              return video.play();
            },
            () => {
              video.load();
              return video.play();
            }
          ];
          
          let strategyIndex = 0;
          const tryNextStrategy = () => {
            if (strategyIndex < strategies.length) {
              const playPromise = strategies[strategyIndex]();
              strategyIndex++;
              
              if (playPromise !== undefined) {
                playPromise.then(() => {
                  this.currentActiveVideo = video;
                }).catch(error => {
                  // Silent fail for autoplay strategies
                });
              } else {
                tryNextStrategy();
              }
            } else {
              // Silent fail for autoplay strategies
              this.setupUserInteractionPlay(video);
            }
          };
          
          tryNextStrategy();
        };
        
        // Try to play immediately
        playVideo();
        
        // Also try when video is ready
        if (video.readyState < 2) {
          video.addEventListener('canplay', () => {
            playVideo();
          }, { once: true });
          
          video.addEventListener('loadedmetadata', () => {
            playVideo();
          }, { once: true });
        }
      } else {
        // Silent fail if no video found
      }
    } else {
      // Silent fail if no active slide found
    }
  }

  setupUserInteractionPlay(video) {
    const playOnInteraction = () => {
      video.muted = true; // Ensure it's muted
      video.play().then(() => {
        this.currentActiveVideo = video;
      }).catch((error) => {
        // Silent fail for user interaction
      });
      document.removeEventListener('click', playOnInteraction);
      document.removeEventListener('touchstart', playOnInteraction);
    };
    
    document.addEventListener('click', playOnInteraction, { once: true });
    document.addEventListener('touchstart', playOnInteraction, { once: true });
  }

  // Method to manually trigger video control (can be called externally)
  playCenterVideo(sliderElement) {
    const swiper = sliderElement.swiper || sliderElement;
    if (swiper) {
      this.playCenterSlideVideo(swiper);
    }
  }

  pauseAllVideosInSlider(sliderElement) {
    const swiper = sliderElement.swiper || sliderElement;
    if (swiper) {
      this.pauseAllVideos(swiper);
    }
  }

  // Method to restart autoplay functionality
  restart() {
    this.isInitialized = false;
    this.currentActiveVideo = null;
    this.mutedVideos.clear();
    this.hoverPlayingVideos.clear();
    this.setupVideoSliders();
  }

  // Method to toggle mute for a specific video (can be called externally)
  toggleVideoMute(videoElement) {
    const videoContainer = videoElement.closest('.video-container');
    const muteButton = videoContainer.querySelector('.mute-btn');
    if (muteButton) {
      this.toggleMute(videoElement, muteButton);
    }
  }
}

// Initialize the video slider autoplay functionality
document.addEventListener('DOMContentLoaded', () => {
  window.videoSliderAutoplay = new VideoSliderAutoplay();
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = VideoSliderAutoplay;
} 